import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { EmojiIcon } from '@mote/assets/icons'
import { Dropdown, VisuallyHidden } from '@mote/ui'
import { ACTIVE_REACTION_EMOJIS } from '@mote/helpers/constants'
import { Emoji } from '../../shared/components'

export const EmojiPicker = ({
  activeEmojis = [],
  onEmoji,
  isNarrow,
  shouldEnableReactions,
  moteId = '',
  ...rest
}) => {
  const handleSelection = React.useCallback(
    (key, event) => {
      event.stopPropagation()

      if (shouldEnableReactions) {
        onEmoji?.(key)
      }
    },
    [shouldEnableReactions, onEmoji]
  )

  const renderEmojis = React.useMemo(() => {
    return ACTIVE_REACTION_EMOJIS.map(({ id, emoji, name, message, label }) => {
      return (
        <DropdownItem key={name}>
          <Emoji
            message={message}
            label={label}
            isAnimated={false}
            isEmojiPickerItem
          >
            {emoji}
          </Emoji>
        </DropdownItem>
      )
    })
  }, [])

  const renderActiveEmojis = React.useMemo(() => {
    return ACTIVE_REACTION_EMOJIS.map(({ id, emoji, message, name }) => {
      const active = activeEmojis.includes(name)

      return active ? (
        <Emoji
          key={name}
          message={message}
          onClick={event => {
            event.stopPropagation()
            onEmoji?.(name)
          }}
          isAnimated={false}
          noTooltip
        >
          {emoji}
        </Emoji>
      ) : null
    }).filter(Boolean)
  }, [activeEmojis])

  return (
    <Root aria-label='Reactions' role='region'>
      <VisuallyHidden>Reactions</VisuallyHidden>
      {renderActiveEmojis.length > 0 && (
        <ActiveEmojisContainer aria-label='Active reactions'>
          <VisuallyHidden>Active reactions</VisuallyHidden>
          {renderActiveEmojis}
        </ActiveEmojisContainer>
      )}

      <StyledDropdown
        id={`mote-player-reactions-${moteId}`}
        onAction={handleSelection}
        triggerElementContent={<EmojiIcon color='currentColor' />}
        placement='top-right'
        showChevron={false}
        aria-label='Mote reactions'
        orientation='horizontal'
        {...rest}
      >
        {renderEmojis}
      </StyledDropdown>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin: 4px 0;
  width: 100%;
`

const ActiveEmojisContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const StyledDropdown = styled(Dropdown)`
  .dropdown-trigger-container {
    padding: 4px;
    border-radius: 100%;
    color: #646f7b;

    &:hover {
      color: #ac0ae8;
    }

    &:active {
      color: #9b09d1;
    }
  }

  .dropdown-container {
    width: max-content;
    padding: 8px;
    border-radius: 24px;
  }
`

const DropdownItem = styled(Dropdown.Item)`
  height: 24px;
  padding: 0;

  span.emoji {
    transition: transform 150ms ease-in;
  }

  &:hover,
  &:focus {
    background: transparent;

    span.emoji {
      cursor: pointer;
      transform: scale(1.25);
    }
  }
`
